import {
  Card,
  CardBody,
  Container,
  CardHeader,
  Stack,
  IconButton,
  useDisclosure,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent, ModalHeader,
  ModalOverlay,
  Heading,
  Input, FormControl,
  FormLabel, useToast,
  ModalFooter,
  FormHelperText
} from "@chakra-ui/react";
import MyBreadcrumb from "components/breadcrumb/Breadcrumb";
import CustomTable from "components/table/CustomTable";
import { DeleteIcon, EditIcon, SmallAddIcon, ViewIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { SourceModalMode } from "types/SourceModalMode";
import { SubmitHandler, useForm } from "react-hook-form";

import { useSelector } from "react-redux";
import { useAppDispatch } from "store/store.hook";
import { deleteSources, patchSources, postSources } from "service/api/sources.api";
import { addSource, deleteSource, updateSource } from "store/reducers/sources.slice";
import { Breadcrumb } from "types/Breadcrumb";


type TableDataSource = {
  _id: string;
  name: string;
  abbr: string;
  code: string;
};

const columnHelper = createColumnHelper<TableDataSource>();

type SourcesPageProps = {
  isUserAdmin: boolean;
};


/**
 * Component representing the Sources Page.
 * 
 * @param {SourcesPageProps} props - The properties for the SourcesPage component.
 * @param {boolean} props.isUserAdmin - Indicates if the user has admin privileges.
 * 
 * @returns {JSX.Element} The rendered SourcesPage component.
 * 
 * This component displays a list of sources and allows users to view, edit, or delete them.
 * Admin users can also add new sources. The component uses a modal for creating, editing,
 * viewing, and deleting sources.
 * 
 * @component
 * 
 * @example
 * // Usage example:
 * <SourcesPage isUserAdmin={true} />
 * 
 * @remarks
 * The component uses the following hooks:
 * - `useToast` for displaying toast notifications.
 * - `useAppDispatch` for dispatching Redux actions.
 * - `useSelector` for selecting state from the Redux store.
 * - `useState` for managing local state.
 * - `useDisclosure` for managing modal state.
 * - `useForm` for managing form state and validation.
 * 
 * The component defines the following functions:
 * - `showModal` for opening the modal with specific item values and mode.
 * - `onSubmit` for handling form submission based on the current mode (create, edit, delete).
 * 
 * The component renders a table with columns for source name, abbreviation, code, and actions.
 * The actions include viewing, editing, and deleting a source, with edit and delete actions
 * being disabled for non-admin users.
 * 
 * The modal contains a form with fields for source name, abbreviation, and code, and buttons
 * for closing the modal, adding a source, saving changes, or deleting a source based on the
 * current mode.
 */
export default function SourcesPage({ isUserAdmin }: SourcesPageProps) {

  const toast = useToast()
  const dispatch = useAppDispatch();
  const attachments = useSelector((state: any) => state.sources.sources);
  const [formValues, setFormValues] = useState<any>({});
  const [formMode, setFormMode] = useState<SourceModalMode>(SourceModalMode.EDIT);
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { register, handleSubmit, control, reset } = useForm({
    defaultValues: {
      name: "",
      _id: "",
      code: "",
      abbr: "",
    },
    values: formValues
  });





  const showModal = (itemValues: any, mode: SourceModalMode = SourceModalMode.EDIT) => {
    setFormMode(mode);
    setFormValues(itemValues);
    onOpen()
  }


  const columns = [
    columnHelper.accessor("name", {
      cell: (info: any) => info.getValue(),
      header: `Titolo Fonte`,
    }),
    columnHelper.accessor("abbr", {
      cell: (info: any) => info.getValue(),
      header: `Abbreviazione`,
    }),
    columnHelper.accessor("code", {
      cell: (info: any) => info.getValue(),
      header: `Codice`,
    }),
    columnHelper.accessor("_id", {
      cell: ({ row }) => (
        <Stack spacing={2} direction="row" align="center">
          <IconButton
            colorScheme="blue"
            aria-label="View"
            size="sm"
            onClick={() => showModal(row.original, SourceModalMode.VIEW)}
            icon={<ViewIcon />}
          />
          <IconButton
            colorScheme="yellow"
            aria-label="Edit"
            size="sm"
            isDisabled={!isUserAdmin}
            onClick={() => showModal(row.original, SourceModalMode.EDIT)}
            icon={<EditIcon />}
          />
          <IconButton
            colorScheme="red"
            aria-label="Delete"
            size="sm"
            isDisabled={!isUserAdmin}
            onClick={() => showModal(row.original, SourceModalMode.DELETE)}
            icon={<DeleteIcon />}
          />
        </Stack>
      ),
      header: ``,
      enableSorting: false,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    }),
  ];



  const onSubmit: SubmitHandler<any> = (data) => {

    const { name, abbr, code } = data;

    if (formMode == SourceModalMode.CREATE) {

      postSources({ name, abbr, code }).then((res) => {

        dispatch(addSource(res))

        toast({
          title: "Fonte aggiunta",
          description: "La fonte è stata aggiunta con successo",
          status: "success",
          duration: 6000,
          isClosable: true,
        })
      }
      ).catch((err) => {
        const errorMsg = err.response.data.message ?? "";
        toast({
          title: "Errore",
          description: `Si è verificato un errore durante l'aggiunta della fonte.\n${errorMsg}`,
          status: "error",
          duration: 6000,
          isClosable: true,
        })
      })


    } else if (formMode == SourceModalMode.EDIT) {

      patchSources(data._id, { name, abbr, code }).then((res) => {
        dispatch(updateSource(res))
        toast({
          title: "Fonte modificata",
          description: "La fonte è stata modificata con successo",
          status: "success",
          duration: 6000,
          isClosable: true,
        })
      }).catch((err) => {
        dispatch(updateSource({ _id: data._id, name, abbr, code }))
        toast({
          title: "Errore",
          description: "Si è verificato un errore durante la modifica della fonte",
          status: "error",
          duration: 6000,
          isClosable: true,
        })
      }
      )
    } else if (formMode == SourceModalMode.DELETE) {

      patchSources(data._id, { isDeleted: true }).then((res) => {
        dispatch(deleteSource({ _id: data._id }))
        toast({
          title: "Fonte eliminata",
          description: "La fonte è stata eliminata con successo",
          status: "success",
          duration: 6000,
          isClosable: true,
        })
      }).catch((err) => {
        toast({
          title: "Errore",
          description: "Si è verificato un errore durante l'eliminazione della fonte",
          status: "error",
          duration: 6000,
          isClosable: true,
        })
      })
    }

    onClose()
  }

  const breadcrumb: Breadcrumb = new Breadcrumb(
    [
      { label: "Fonti", link: "/sources", value: "", isActive: false },
    ]
  );

  return (
    <>

      <Modal isOpen={isOpen} onClose={onClose} size={"6xl"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {
              formMode == SourceModalMode.CREATE && "Aggiungi fonte"
            }
            {
              formMode == SourceModalMode.EDIT && "Modifica fonte " + formValues.name
            }
            {
              formMode == SourceModalMode.DELETE && "Elimina fonte " + formValues.name
            }
            {
              formMode == SourceModalMode.VIEW && "Specifica " + formValues.name
            }
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>

            <FormControl mb={5} isRequired>
              <FormLabel>Titolo</FormLabel>
              <Input placeholder='Inserisci il titolo' size='md' required={true}
                isRequired={true}
                isReadOnly={formMode == SourceModalMode.DELETE}
                {
                ...register(`name`, { required: true })
                }
              />
            </FormControl>

            <FormControl mb={5} isRequired>
              <FormLabel>Abbreviazione</FormLabel>
              <Input placeholder="Inserisci un'abbreviazione" size='md' required={true}
                isRequired={true}
                isReadOnly={formMode == SourceModalMode.DELETE}
                {
                ...register(`abbr`, { required: true })
                }
              />
            </FormControl>

            <FormControl mb={5} isRequired>
              <FormLabel htmlFor="input-code" display={"inline"}>Codice</FormLabel><FormHelperText display={"inline"}>(solo valori alfanumerici ed univoci)</FormHelperText>
              <Input placeholder='Inserisci un codice identificativo univoco' size='md' required={true}
                id="input-code"
                pattern="[A-Za-z0-9]{1,64}"
                isRequired={true}
                isReadOnly={formMode == SourceModalMode.DELETE}
                {
                ...register(`code`, { required: true })
                }
              />
            </FormControl>

            <FormControl mb={5} isRequired>
              <Input type="hidden"
                {
                ...register(`_id`, { required: false })
                }
              />
            </FormControl>


          </ModalBody>

          <ModalFooter>
            <Button mr={3} onClick={onClose}>
              Chiudi
            </Button>
            {
              formMode == SourceModalMode.DELETE &&
              <Button colorScheme='red' mr={3} onClick={handleSubmit(onSubmit)}>
                Elimina
              </Button>
            }
            {
              formMode == SourceModalMode.CREATE &&
              <Button colorScheme='green' mr={3} onClick={handleSubmit(onSubmit)}>
                Aggiungi fonte
              </Button>
            }
            {
              formMode == SourceModalMode.EDIT &&
              <Button colorScheme='green' mr={3} onClick={handleSubmit(onSubmit)}>
                Salva modifiche
              </Button>
            }
          </ModalFooter>

        </ModalContent>
      </Modal>

      <section className="dashboard-first-section">
        <Container maxW="container.lg" maxH={"container.xl"} textAlign={"left"}>
          <MyBreadcrumb breadcrumb={breadcrumb} />

          <Card minH={"60vh"}>
            <CardHeader>
              <Heading as="h1" size="xl" >
                Fonti
              </Heading>
              <Button colorScheme="green" size={"xs"} alignContent={"end"} float={"right"} disabled={true} onClick={() => showModal({}, SourceModalMode.CREATE)}>
                Aggiungi fonte
                <SmallAddIcon />
              </Button>
            </CardHeader>
            <CardBody>
              <CustomTable data={attachments} columns={columns} />
            </CardBody>
          </Card>
        </Container>
      </section>
    </>
  );
}